import { Button, Link } from '@rbilabs/universal-components';
import { Image, ImageBackground } from 'expo-image';
import { useRouter } from 'expo-router';
import { StyleSheet, View } from 'react-native';
import { Typography } from 'ucl/components/text/Typography';

import { CircularProgress } from 'components/circular-progress';
import { LoyaltyQuestStatus } from 'generated/graphql-gateway';
import { routes } from 'navigation/routing';
import { t } from 'state/intl/translations';
import { primitive, spacing } from 'styles/constants';

import { RedeemButton } from '../profile-card/components/styled';
import { styles as sharedStyles } from '../styles';

const crown = require('assets/images/nav/rewards-active.svg');

const megaphone = require('./user-crown.png');

export const ChallengeProgressCard = ({
  crownsAmount,
  challengeStatus,
  completedSteps,
  totalSteps,
  questCmsId,
}: {
  crownsAmount: number;
  challengeStatus: LoyaltyQuestStatus;
  completedSteps: number;
  totalSteps: number;
  questCmsId?: string;
}) => {
  const { navigate } = useRouter();

  const TITLE_BY_STATUS = {
    [LoyaltyQuestStatus.NOT_ENROLLED]: t('finishYourProfile', 'Finish Your Profile Completion'),
    [LoyaltyQuestStatus.ACTIVATED]: t('finishYourProfile', 'Finish Your Profile Completion'),
    [LoyaltyQuestStatus.NOT_COMPLETED]: t('keepGoing', "Keep Going — You're Close!"),
    [LoyaltyQuestStatus.COMPLETED]: `${t('profileCompleted', 'Profile Completed')} 🎉`,
  };

  const DESCRIPTION_BY_STATUS = {
    [LoyaltyQuestStatus.NOT_ENROLLED]: t('earnCrownsToUnlockRewards', {
      crowns: crownsAmount,
      defaultValue: 'Earn {crowns} Crowns and unlock exclusive rewards',
    }),
    [LoyaltyQuestStatus.ACTIVATED]: t('earnCrownsToUnlockRewards', {
      crowns: crownsAmount,
      defaultValue: 'Earn {crowns} Crowns and unlock exclusive rewards',
    }),
    [LoyaltyQuestStatus.NOT_COMPLETED]: t('finishProfileToEarnCrowns', {
      crowns: crownsAmount,
      defaultValue: 'Finish your profile to earn {crowns} Crowns',
    }),
    [LoyaltyQuestStatus.COMPLETED]: t('youEarnedCrowns', {
      crowns: crownsAmount,
      defaultValue: 'You earned {crowns} Crowns! Tap below to claim your reward.',
    }),
  };

  const isChallengeCompleted = challengeStatus === LoyaltyQuestStatus.COMPLETED;
  const showStepProgress = completedSteps > 0;

  const leftTag = (
    <View style={[styles.baseTag, isChallengeCompleted && styles.earnedCrownsTag]}>
      <Image
        source={crown}
        style={styles.tagIcon}
        tintColor={isChallengeCompleted ? primitive.$bbqBrown : primitive.$meltyYellow}
      />
      <Typography fontSize="2xs">
        {isChallengeCompleted
          ? t('amountEarned', {
              crowns: crownsAmount,
              defaultValue: '{crowns} earned',
            })
          : t('earnAmount', {
              crowns: crownsAmount,
              defaultValue: 'Earn {crowns}',
            })}
      </Typography>
    </View>
  );

  return (
    <>
      <View style={[styles.card, sharedStyles.cardElevationStyles]}>
        {showStepProgress ? (
          <View style={styles.left}>
            {leftTag}
            <CircularProgress
              size={90}
              bgColor="rgba(255, 255, 255, 0.3)"
              pgColor="white"
              strokeWidth={8}
              progressPercent={(completedSteps / totalSteps) * 100}
              contentStyle={styles.circularProgressContent}
            >
              <Typography color="white" fontFamily="heading" fontSize="3xl" lineHeight="3xl">
                {completedSteps}{' '}
              </Typography>
              <Typography color="white" fontFamily="heading" fontSize="lg" lineHeight="lg">
                / {totalSteps}
              </Typography>
            </CircularProgress>
          </View>
        ) : (
          <ImageBackground style={[styles.left, styles.leftNoProgress]} source={megaphone}>
            {leftTag}
          </ImageBackground>
        )}
        <View style={styles.right}>
          <Typography fontFamily="heading" fontSize="sm">
            {TITLE_BY_STATUS[challengeStatus]}
          </Typography>
          <Typography fontSize="xs">{DESCRIPTION_BY_STATUS[challengeStatus]}</Typography>
          <View style={styles.footer}>
            {challengeStatus === LoyaltyQuestStatus.COMPLETED ? (
              <Button
                variant="outline"
                size="sm"
                width="100%"
                accessibilityRole="link"
                onPress={() => navigate(routes.rewards)}
              >
                {t('claimReward', 'Claim Reward')}
              </Button>
            ) : (
              <>
                <RedeemButton
                  style={styles.footerHalfWidthButton}
                  accessibilityRole="link"
                  onPress={() => navigate('/challenges/user-journey')}
                >
                  {t('complete', 'Complete')}
                </RedeemButton>
                {questCmsId && (
                  <Link
                    _text={{ fontSize: 'xs' }}
                    style={styles.footerHalfWidthButton}
                    onPress={() => navigate(`${routes.questTerms}?cmsId=${questCmsId}`)}
                  >
                    {t('viewTerms', 'View Terms')}
                  </Link>
                )}
              </>
            )}
          </View>
        </View>
      </View>
    </>
  );
};

const styles = StyleSheet.create({
  card: {
    flexDirection: 'row',
    backgroundColor: primitive.$white,
    borderRadius: 12,
    overflow: 'hidden',
  },
  left: {
    backgroundColor: primitive.$bbqBrown,
    paddingHorizontal: spacing.$5,
    paddingVertical: spacing.$6,
    position: 'relative',
    justifyContent: 'center',
    flex: 1,
    maxWidth: 145,
    minWidth: 125,
  },
  leftNoProgress: {
    paddingHorizontal: 0,
    paddingVertical: 0,
  },
  right: {
    paddingHorizontal: spacing.$4,
    paddingVertical: spacing.$6,
    gap: spacing.$2,
    flex: 1,
    minWidth: 200,
  },
  footer: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.$4,
  },
  footerHalfWidthButton: {
    flex: 1,
  },
  baseTag: {
    borderRadius: 4,
    backgroundColor: primitive.$white,
    flexDirection: 'row',
    gap: spacing.$1,
    alignItems: 'center',
    paddingHorizontal: 6,
    position: 'absolute',
    top: 8,
    left: 8,
    zIndex: 1,
  },
  earnedCrownsTag: {
    backgroundColor: primitive.$meltyYellow,
  },
  tagIcon: {
    width: 12,
    height: 12,
  },
  circularProgressContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
});
